import { isFunction } from 'lodash-es'

import type { ConfirmationHandlers } from '@/domains/command-center/handlers/product/productCommands.types'
import type {
  ActionCategory,
  ActionCommand,
  MenuItem,
  SearchResultCommand,
  WorkflowState
} from '@/domains/command-center/types'

import { LOCKED_REASON } from '@/domains/command-center/commandRegistry'
import { NAVIGATION_CATEGORY_ID } from '@/domains/command-center/handlers/navigation/navigationCommands'
import {
  createAddVariantsAction,
  createProductFamilyAction
} from '@/domains/command-center/handlers/product/productCommands'
import { createUpdateStockForMatchAction } from '@/domains/command-center/handlers/stock/stockCommands'
import { STOCK_CATEGORY_ID, UPDATE_STOCK_FOR_MATCH_ID } from '@/domains/command-center/handlers/stock/stockMode.consts'
import { SEARCH_SCOPES } from '@/domains/command-center/services/searchScopes'
import { scoreCommand } from '@/domains/command-center/utils/commandMatcher'
import { getCancelAction, getPendingBarcodesHeader } from '@/domains/command-center/workflow/workflowManager'

/** A starting value. Tune it against real use rather than argument. */
const MAX_PAGE_ROWS = 8

/**
 * A result group is named by its pill, so the heading and the control that produced it read the
 * same. Anything unmapped falls back to its own key rather than disappearing.
 */
const SCOPE_LABELS: Record<string, string> = Object.fromEntries(SEARCH_SCOPES.map(scope => [scope.id, scope.label]))

const scopeGroupLabel = (resultType: string): string => SCOPE_LABELS[resultType] ?? resultType

/** The row a scan gets when the Products pill is off. Selecting it switches the pill on. */
const SCAN_BLOCKED_ROW_ID = 'scan-blocked-by-scope'

interface ScoredAction {
  action: ActionCommand
  score: number
}

const generateCommands = (
  query: string | undefined,
  currentBarcodes: string[],
  searchResults: SearchResultCommand[],
  workflowState: WorkflowState,
  resetWorkflow: () => void,
  clearQuery: () => void,
  isSearchLoading: boolean,
  waitForSearchComplete: (() => Promise<SearchResultCommand[]>) | undefined,
  confirmationHandlers: ConfirmationHandlers | undefined,
  // Last, and required. A default here would hand back a palette with no rows
  // and no error — the one wiring mistake this function can make silently.
  categories: ActionCategory[]
): (ActionCommand | SearchResultCommand)[] => {
  const commands: (ActionCommand | SearchResultCommand)[] = []

  // Add search results
  commands.push(...searchResults)

  if (workflowState.mode === 'idle') {
    // Process each category
    categories.forEach(category => {
      if (category.id === 'cat-products') {
        // The two barcode actions are built here rather than declared on the category, so a locked
        // category cannot reach them — they are locked by hand, or they stay live for a user who
        // may not create products at all.
        const lock = (action: ActionCommand): ActionCommand =>
          category.isLocked ? { ...action, disabled: true, subLabel: LOCKED_REASON } : action

        // Add dynamic barcode-related actions (pass search results and loading state for SKU match detection)
        commands.push(
          lock(
            createProductFamilyAction(
              currentBarcodes,
              clearQuery,
              searchResults,
              isSearchLoading,
              waitForSearchComplete,
              confirmationHandlers
            )
          )
        )
        commands.push(lock(createAddVariantsAction(currentBarcodes)))

        // Add remaining static product actions (except barcode-specific ones)
        category.actions
          .filter(action => !['action-generate-family-static'].includes(action.id))
          .forEach(action => commands.push({ ...action }))
      } else if (category.id === STOCK_CATEGORY_ID) {
        // The second category whose rows depend on the results; the products branch above is its twin. A third
        // earns a hook the category owns, rather than another branch here.
        category.actions.forEach(action => commands.push({ ...action }))

        // A scan's own way into stock mode. Left out, not locked, for a user who may not adjust:
        // a locked row on every scan would be noise, and the plain command already says why.
        const scanEntry = category.isLocked ? null : createUpdateStockForMatchAction(searchResults, query)
        if (scanEntry) commands.push(scanEntry)
      } else {
        // Add all actions from other categories
        category.actions.forEach(action => commands.push({ ...action }))
      }
    })
  } else if (workflowState.mode === 'awaitingParentProduct') {
    // Add cancel action when in parent product selection mode
    commands.push(getCancelAction(resetWorkflow))
  }

  return commands
}

const generateMenuItems = (
  query: string | undefined,
  currentBarcodes: string[],
  searchResults: SearchResultCommand[],
  allCommands: (ActionCommand | SearchResultCommand)[],
  workflowState: WorkflowState,
  loading: boolean,
  categories: ActionCategory[],
  scanBlockedBarcode: string | undefined = undefined,
  isProductScopeActive = true
): MenuItem[] => {
  const q = query?.trim() ?? ''

  if (workflowState.mode === 'awaitingParentProduct') {
    return generateParentProductSelectionMenu(q, searchResults, loading, workflowState, allCommands)
  }

  return generateIdleModeMenu(
    q,
    currentBarcodes,
    searchResults,
    allCommands,
    loading,
    categories,
    scanBlockedBarcode,
    isProductScopeActive
  )
}

const generateParentProductSelectionMenu = (
  query: string,
  searchResults: SearchResultCommand[],
  loading: boolean,
  workflowState: WorkflowState,
  allCommands: (ActionCommand | SearchResultCommand)[]
): MenuItem[] => {
  const output: MenuItem[] = []

  // Header showing pending barcodes
  output.push(getPendingBarcodesHeader(workflowState.pendingBarcodesForVariants) as MenuItem)

  // Display search results for parent products
  if (searchResults.length > 0) {
    output.push({
      label: 'Select Parent Product',
      items: searchResults.map(sr => ({ ...sr })) as MenuItem[]
    })
  }

  // Loading or no results indicators
  if (loading && query) {
    output.push({
      id: 'loading-parent-search',
      label: 'Searching for parent product...',
      icon: 'loading',
      disabled: true
    })
  } else if (query && searchResults.length === 0 && !loading) {
    output.push({
      id: 'no-parent-found',
      label: `No parent products found for "${query}"`,
      disabled: true
    })
  }

  // Add the cancel action
  const cancelAction = allCommands.find(cmd => cmd.id === 'action-cancel-add-variants')
  if (cancelAction) output.push(cancelAction as MenuItem)

  return output
}

const generateIdleModeMenu = (
  query: string,
  currentBarcodes: string[],
  searchResults: SearchResultCommand[],
  allCommands: (ActionCommand | SearchResultCommand)[],
  loading: boolean,
  categories: ActionCategory[],
  scanBlockedBarcode: string | undefined = undefined,
  isProductScopeActive = true
): MenuItem[] => {
  const output: MenuItem[] = []
  let hasContentGroups = false

  // 1. Process search results by scope. `searchItems` concatenates in scope order and object keys
  // keep insertion order, so the groups arrive in pill order without being sorted again.
  if (query && searchResults.length > 0) {
    const groupedResults = groupSearchResultsByType(searchResults)

    for (const resultType in groupedResults) {
      output.push({
        label: scopeGroupLabel(resultType),
        items: groupedResults[resultType].map(sr => ({ ...sr })) as MenuItem[]
      })
    }

    if (output.length > 0) hasContentGroups = true
  }

  // 2. Group actions by category
  categories.forEach(category => {
    const matchingActions = getMatchingActionsForCategory(category, allCommands, query, currentBarcodes)

    if (matchingActions.length > 0) {
      output.push({
        label: category.label,
        items: matchingActions.map(a => ({
          id: a.id,
          label: a.label,
          // Carries the sidebar section on a navigation row. Dropping it here
          // left every page row anonymous.
          subLabel: a.subLabel,
          icon: a.icon,
          disabled: a.disabled,
          onSuccess: a.onSuccess,
          onSelect: a.onSelect
        })) as MenuItem[]
      })

      hasContentGroups = true
    }
  })

  // 3. Add loading or no results indicators
  const qDisplay = query.length > 30 ? `${query.substring(0, 30)}...` : query

  // A scan answered with silence reads as a broken scanner. Name the cause instead, above
  // everything else, and make the fix the row itself.
  //
  // Two conditions, and both are the row's own meaning rather than bookkeeping.
  //
  // The blocked code must still BE the query: a flag had no way to know what it described, so the
  // row outlived the query it named and read "Switch Products on to look up """.
  //
  // And products must still be absent from the search. Switching the pill on answers the row, so
  // it goes — without this it sat above the very product it said could not be found.
  if (!isProductScopeActive && scanBlockedBarcode && query === scanBlockedBarcode) {
    output.unshift({
      id: SCAN_BLOCKED_ROW_ID,
      label: 'Products is not in this search',
      subLabel: `Switch Products on to look up "${qDisplay}"`,
      icon: 'inventory_2'
    } as MenuItem)

    return output
  }

  if (loading && query) {
    output.unshift({
      id: 'loading-indicator',
      label: 'Searching...',
      icon: 'loading',
      disabled: true
    })
  } else if (query && currentBarcodes.length > 0 && searchResults.length === 0 && isProductScopeActive) {
    // A scanned/typed barcode that matched no product — surface it at the top,
    // above the product commands, so the user knows the lookup came up empty.
    //
    // ⚠ Only when products were actually searched. With the Products pill off this row claimed a
    // barcode was free when nothing had looked for it, which is the opposite of the truth. The
    // generic "No results found" below is honest about the scopes that did run.
    output.unshift({
      id: 'no-exact-match',
      label: `No exact match for "${qDisplay}"`,
      disabled: true
    })
  } else if (query && !hasContentGroups && searchResults.length === 0) {
    output.push({
      id: 'no-results',
      label: `No results found for "${qDisplay}"`,
      disabled: true
    })
  }

  return output
}

const groupSearchResultsByType = (searchResults: SearchResultCommand[]): Record<string, SearchResultCommand[]> => {
  const grouped: Record<string, SearchResultCommand[]> = {}

  searchResults.forEach(result => {
    const key = result.resultType || 'other'
    if (!grouped[key]) grouped[key] = []
    grouped[key].push(result)
  })

  return grouped
}

const getMatchingActionsForCategory = (
  category: ActionCategory,
  allCommands: (ActionCommand | SearchResultCommand)[],
  query: string,
  currentBarcodes: string[]
): ActionCommand[] => {
  const matchingActions: ActionCommand[] = []

  // Find all effective commands for this category
  category.actions.forEach(staticDef => {
    const effectiveAction = allCommands.find(cmd => cmd.id === staticDef.id && cmd.type === 'action') as
      | ActionCommand
      | undefined

    if (effectiveAction) {
      matchingActions.push(effectiveAction)
    }
  })

  // Special handling for Products category - add dynamic barcode action
  if (category.id === 'cat-products' && currentBarcodes.length > 0) {
    const initiateAddAction = allCommands.find(cmd => cmd.id === 'action-initiate-add-variants') as
      | ActionCommand
      | undefined

    if (initiateAddAction && !matchingActions.find(a => a.id === initiateAddAction.id)) {
      matchingActions.push(initiateAddAction)
    }
  }

  // Filter actions that match the current query
  const scored = matchingActions
    .map(action => ({ action, score: scoreCommand(action, query, currentBarcodes.length > 0) }))
    .filter(({ score }) => score > 0)

  if (category.id === STOCK_CATEGORY_ID) {
    // The scan row is built from the results, not the query, so the query never scores it. A code
    // scores nothing against "Update Stock for …", and the row would never show.
    const scanEntry = allCommands.find(cmd => cmd.id === UPDATE_STOCK_FOR_MATCH_ID) as ActionCommand | undefined
    const declared = scored.map(({ action }) => action)

    return scanEntry ? [scanEntry, ...declared] : declared
  }

  if (category.id !== NAVIGATION_CATEGORY_ID) {
    // The curated categories keep their declared order. Their order is editorial
    // — "Create a New Product" before the barcode actions — and scoring it would
    // reshuffle a list somebody chose.
    return scored.map(({ action }) => action)
  }

  // An empty query scores every page the same, so ranking them would show eight
  // arbitrary ones the moment the palette opens. Pages answer a question; until
  // one is asked, the curated actions have the space to themselves.
  if (!query) {
    return []
  }

  return rankPages(scored)
}

/**
 * ~86 pages share one group, so an unranked list buries the obvious answer. Sort
 * loses to a shorter label on a tie, which puts "Printers" above "Printer
 * Reports", then cap it: past a handful of rows nobody reads, and the curated
 * categories below would be pushed off screen.
 */
const rankPages = (scored: ScoredAction[]): ActionCommand[] =>
  [...scored]
    .sort((a, b) => b.score - a.score || labelLength(a.action) - labelLength(b.action))
    .slice(0, MAX_PAGE_ROWS)
    .map(({ action }) => action)

const labelLength = (action: ActionCommand): number =>
  (isFunction(action.label) ? action.label() : (action.label ?? '')).length

export {
  SCAN_BLOCKED_ROW_ID,
  generateCommands,
  generateMenuItems,
  generateParentProductSelectionMenu,
  generateIdleModeMenu,
  groupSearchResultsByType,
  getMatchingActionsForCategory
}
